import {
  toWSDateString,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import {
  Currency,
  PayerRow,
  RecurringInvoice
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { calculateRecurringLineItemsTotal } from "../../../../Invoices/utils";
import { getRecurringInvoiceStatusPillProps } from "../../../../Invoices/utils/getRecurringInvoiceStatusPillProps";
import { selectorRecurrenceCadenceLabel } from "../../../../shared/selectors/selectorRecurrenceCadenceLabel";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { getLocalStartDayByUTC } from "../../../../utils/dates";
import { buildIcon } from "./buildIcon";

type GetColumnsOptions = {
  payerRowById: Map<string | undefined, PayerRow>;
};

const selectorNextSendDate = (
  recurringInvoice: RecurringInvoice
): Date | undefined =>
  recurringInvoice.nextOccurrenceDate ??
  recurringInvoice.schedule?.scheduleDates?.[0]?.date ??
  recurringInvoice.schedule?.anchorDate;

export const getColumns = (
  options: GetColumnsOptions
): WSTableColumn<RecurringInvoice>[] => [
  {
    config: {
      header: { text: "Client" },
      gridTemplateSizeMax: "2fr"
    },
    renderFunction: ({ data: recurringInvoice }) => {
      const payerRow = options.payerRowById.get(recurringInvoice.payerId);

      return (
        <WSTableCell
          avatar={{ type: "icon", ...buildIcon(recurringInvoice) }}
          text={payerRow ? selectorSearchRowName(payerRow) : undefined}
          secondaryText={payerRow?.email ?? undefined}
        />
      );
    }
  },
  {
    config: {
      hideOnScreens: ["XS", "S"],
      header: { text: "Cadence" },
      gridTemplateSizeMax: "1fr"
    },
    renderFunction: ({ data: recurringInvoice }) => (
      <WSTableCell
        text={selectorRecurrenceCadenceLabel(recurringInvoice.schedule)}
      />
    )
  },
  {
    config: {
      hideOnScreens: ["XS", "S", "M"],
      header: { text: "Next send date" },
      gridTemplateSizeMax: "1fr"
    },
    renderFunction: ({ data: recurringInvoice }) => {
      const nextSendDate = selectorNextSendDate(recurringInvoice);

      return (
        <WSTableCell
          text={
            nextSendDate
              ? toWSDateString(
                  getLocalStartDayByUTC(nextSendDate),
                  "monthDayYear"
                )
              : ""
          }
        />
      );
    }
  },
  {
    config: {
      header: { text: "Amount" },
      justify: "end",
      gridTemplateSizeMax: "1fr"
    },
    renderFunction: ({ data: recurringInvoice }) => (
      <WSTableCell
        text={formatMoney(
          calculateRecurringLineItemsTotal(
            recurringInvoice.invoiceTemplate?.lineItems
          ),
          recurringInvoice.currency as Currency
        )}
      />
    )
  },
  {
    config: {
      hideOnScreens: ["XS"],
      header: { text: "Status" },
      gridTemplateSizeMax: "min-content"
    },
    renderFunction: ({ data: recurringInvoice }) => (
      <WSTableCell
        pill={getRecurringInvoiceStatusPillProps(recurringInvoice)}
      />
    )
  }
];
